import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useParams } from 'react-router-dom';
import Home from './pages/Home';
import Specialties from './pages/Specialties';
import SpecialtyDetail from './pages/SpecialtyDetail';
import PlaceList from './components/PlaceList';
import Redirect from './components/Redirect';
import Privacy from './pages/Privacy';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import { BASE_URL } from './services/api';
import Terminos from './pages/Terminos';

function App() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [routeExists, setRouteExists] = useState(true);
  const location = useLocation();
  const [routesLoaded, setRoutesLoaded] = useState(false);

  const isStaticRoute = ['/', '/especialidades-medicas', '/privacidad', '/terminos'].includes(location.pathname) || 
                       location.pathname.startsWith('/especialidades-medicas/en-detalle/') ||
                       location.pathname === '/redirect';

  useEffect(() => {
    const loadRoutes = async () => {
      console.log('Current pathname:', location.pathname);
      
      // Handle static routes
      if (isStaticRoute) {
        setLoading(false);
        setRouteExists(true);
        setRoutes([]);
        return;
      }

      // Extract specialty from URL for non-static routes
      const pathParts = location.pathname.split('/');
      const specialtyPart = pathParts[2]?.split('-en-')[0];
      
      console.log('Extracted specialty from path:', specialtyPart);

      if (!specialtyPart) {
        setLoading(false);
        setRouteExists(false);
        setRoutes([]);
        return;
      }

      try {
        const response = await fetch(`${BASE_URL}/routes?specialty=${specialtyPart}`);
        if (!response.ok) {
          throw new Error('Error al cargar rutas');
        }
        
        const data = await response.json();
        if (data.success) {
          console.log(`Loaded ${data.routes.length} routes for ${specialtyPart}`);
          setRoutes(data.routes);
          // Check if current path exists in loaded routes
          const exists = data.routes.some(route => route.path === location.pathname);
          console.log(`Route check - Path: ${location.pathname}, Routes loaded: ${data.routes.length}, Exists: ${exists}`);
          setRouteExists(exists);
        } else {
          console.error('Error loading routes:', data.message);
          setRouteExists(false);
          setRoutes([]);
        }
      } catch (error) {
        console.error('Error loading routes:', error);
        setRouteExists(false);
        setRoutes([]);
      } finally {
        setLoading(false);
      }
    };

    loadRoutes();
  }, [location.pathname, isStaticRoute]);

  useEffect(() => {
    const loadInitialRoutes = async () => {
      try {
        const specialtyPart = window.location.pathname.split('/')[2]?.split('-')[0] || 'ginecologos';
        const response = await fetch(`${BASE_URL}/routes?specialty=${specialtyPart}`);
        const data = await response.json();
        if (data.success && Array.isArray(data.routes)) {
          console.log(`Loaded ${data.routes.length} routes for ${specialtyPart}`);
          setRoutesLoaded(true);
        }
      } catch (error) {
        console.error('Error loading initial routes:', error);
        setRoutesLoaded(true); // Set to true even on error to allow fallback rendering
      }
    };

    loadInitialRoutes();
  }, []);

  if (!routesLoaded) {
    return <div className="loading">Cargando...</div>;
  }

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando rutas...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!routeExists && !isStaticRoute) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Página no encontrada</h1>
            <p className="text-gray-600">La página que buscas no existe o ha sido movida.</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <div id="ssr-title-container" suppressHydrationWarning />
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/especialidades-medicas" element={<Specialties />} />
          <Route path="/especialidades-medicas/en-detalle/:specialty" element={<SpecialtyDetail />} />
          <Route path="/redirect" element={<Redirect />} />
          <Route path="/privacidad" element={<Privacy />} />
          <Route path="/terminos" element={<Terminos />} />
          {routes.map((route, index) => {
            const pathParts = route.path.split('/');
            const specialtyPart = pathParts[2]?.split('-en-')[0];
            return (
              <Route 
                key={`${route.path}-${index}`}
                path={route.path}
                element={<PlaceList specialty={specialtyPart} />}
              />
            );
          })}
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App; 